import { useId, useState, type ReactNode } from 'react';
import { EmptyState } from './EmptyState.js';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import { Tabs } from './Tabs.js';
import './HelpPanel.css';

/**
 * One step in a how-to, with the controls it involves called out by name.
 *
 * ⚠️ **`actions` are TAGS, not links, and that is deliberate.** They name the control a step uses —
 * *"Set the id and secret"*, *"Dry run"* — so a reader can find it on the screen beside them. Making
 * them clickable would mean help that operates the product, which is a different and much larger
 * thing: it would need to know each screen's internals, and a stale tag would then be a control that
 * does nothing rather than a word that is merely out of date. The panel is docked next to the screen
 * precisely so naming a control is enough.
 */
export interface HelpStep {
  /** What to do, in the imperative. */
  label: string;
  /** Why, or what to watch for. Optional — a step that needs no caveat should not invent one. */
  detail?: ReactNode;
  /** Controls this step uses, rendered as tags. */
  actions?: string[];
}

export interface HelpTopic {
  title: string;
  body: ReactNode;
  /**
   * A how-to, rendered as a numbered list under the prose.
   *
   * ⚠️ Separate from `body` rather than prose-with-a-list inside it, so the steps are *data*: they
   * can be counted, they carry their own action tags, and a screen with no procedure simply omits
   * them instead of a paragraph pretending to be a list.
   */
  steps?: HelpStep[];
  /** The prototype's "Plain-language glossary" block under the topic. */
  glossary?: { term: string; definition: ReactNode }[];
}

export interface HelpPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current screen's help content, looked up by the caller (e.g. by
   * route) and handed in — this component does not know about routing.
   * `undefined` renders the documented empty state rather than a blank
   * panel, for a screen that hasn't had its help copy written yet. */
  topic: HelpTopic | undefined;
  /**
   * Whether the panel is enlarged to roughly half the viewport.
   *
   * ⚠️ **Controlled by the caller, not held here.** The width is a preference a person expresses
   * once and expects to persist across screens; state inside this component resets every time the
   * panel unmounts (it returns `null` when closed), so an operator who widened it would find it
   * narrow again on the next screen. `useHelpDock` owns it and remembers it.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * The "Ask Dedupr" tab's content — an `Assistant`.
   *
   * **Every product app is expected to pass this** (owner decision 2026-07-29:
   * the `?` icon shows two tabs, Documentation and Ask Dedupr, and all frontends
   * behave identically). Omitting it still degrades to a single-heading panel, so
   * a surface genuinely without an assistant is not broken — but a *product* app
   * omitting it is the inconsistency that decision exists to remove.
   * `apps/admin` did exactly that and shipped a visibly different help panel from
   * the two customer apps.
   */
  assistant?: ReactNode;
}

/**
 * The screen-aware contextual help panel (F29 ui-spec, `/goal`-mandated:
 * "sidebar with inline help in ui"). **Where it is opened from changed on
 * 2026-07-28** (owner decision): the topbar's "Help and guidance" circle, not a
 * pinned row in the nav — the prototype has exactly one help affordance and the
 * build briefly had two. The requirement's substance is unchanged, and is now
 * enforced by `AppShell` refusing to be constructed without `onHelpClick`.
 *
 * **Docked, not an overlay drawer.** The prototype renders this as an inline
 * `<aside>` with a left hairline, no scrim and no shadow, which *shrinks the main
 * column* rather than floating over it — so the help and the screen it describes
 * are readable at the same time. That is the entire point of contextual help, and
 * rendering it as a Radix dialog (scrim, focus trap, content hidden behind it)
 * defeated it (audit S-8). No focus trap for the same reason: this is a companion
 * pane, not a modal.
 *
 * The tab pair ("Documentation" / "Ask Dedupr") and the glossary block are the
 * prototype's own structure, both previously missing. The first tab was labelled
 * "On this page" until 2026-07-29; it is "Documentation" by owner decision, which
 * also reads better beside "Ask Dedupr" — one names a source, the other an action.
 */
export function HelpPanel({
  open,
  onOpenChange,
  topic,
  assistant,
  expanded = false,
  onExpandedChange,
}: HelpPanelProps) {
  const [tab, setTab] = useState('docs');
  /* Ids are generated here rather than inside `Tabs` because the panel has to
     point back at the selected tab with `aria-labelledby`, and it cannot guess a
     value React generated in a child. Without this pair the `tablist` role
     announced "tab, 1 of 2" and led nowhere — the role promises a `tabpanel` and
     nothing linked them. */
  const baseId = useId();
  const tabsId = `${baseId}-tabs`;
  const panelId = `${baseId}-panel`;
  if (!open) return null;

  const showTabs = Boolean(assistant);

  return (
    <aside
      className={`dpr-help-panel${expanded ? ' dpr-help-panel--expanded' : ''}`}
      aria-label="Help and guidance"
    >
      <div className="dpr-help-panel__head">
        {showTabs ? (
          <Tabs
            label="Help view"
            value={tab}
            onChange={setTab}
            idPrefix={tabsId}
            controls={panelId}
            items={[
              { id: 'docs', label: 'Documentation' },
              { id: 'ask', label: 'Ask Dedupr' },
            ]}
          />
        ) : (
          <h2 className="dpr-help-panel__title">Documentation</h2>
        )}
        {/* ⚠️ The two controls are ONE group, not two children of the head. The head is
            `space-between`, so three siblings strand the widen chevron in the middle of the
            header — invisible at 348px, where the tabs wrap and the two happen to end up
            adjacent, and obvious the moment the panel is widened. Found in a browser at 50vw. */}
        <div className="dpr-help-panel__controls">
        {/* ── Widen / narrow ──────────────────────────────────────────────────────────────────
            ⚠️ Only rendered when the caller can actually remember the choice. A toggle whose state
            resets on the next screen is worse than none: the reader concludes it did not work. */}
        {onExpandedChange ? (
          <IconButton
            size="sm"
            variant="ghost"
            /* ⚠️ `chevronRight` rotated by CSS, because the catalogue has no `chevronLeft` and one
               arrow in two orientations is not worth a new icon — the ui-spec's stated goal is for
               this console to be the cheapest consumer of the design system, not a driver of new
               primitives. The panel is docked right, so widening it grows LEFTWARD: the chevron
               points the way the edge will move. */
            icon={
              <span className={expanded ? '' : 'dpr-help-panel__chev--flip'}>
                <Icon name="chevronRight" size={16} />
              </span>
            }
            /* Names the RESULT, not the mechanism — "Expand" alone leaves a screen-reader user to
               guess what expands and in which direction. */
            label={expanded ? 'Narrow the help panel' : 'Widen the help panel to half the screen'}
            aria-pressed={expanded}
            onClick={() => onExpandedChange(!expanded)}
          />
        ) : null}
        <IconButton
          size="sm"
          variant="ghost"
          icon={<Icon name="close" size={16} />}
          label="Close help panel"
          onClick={() => onOpenChange(false)}
        />
        </div>
      </div>

      <div
        className="dpr-help-panel__body"
        id={panelId}
        role={showTabs ? 'tabpanel' : undefined}
        aria-labelledby={showTabs ? `${tabsId}-${tab}` : undefined}
      >
        {showTabs && tab === 'ask' ? (
          assistant
        ) : topic ? (
          <>
            <div className="dpr-help-panel__topic">
              <h3 className="dpr-help-panel__topic-title">{topic.title}</h3>
              <div className="dpr-help-panel__topic-body">{topic.body}</div>
            </div>
            {/* ── The how-to ───────────────────────────────────────────────────────────────────
                An ordered list, so a reader can say "I am on step 3" — and each step names the
                controls it uses as tags, which is what makes the docked panel usable beside the
                screen rather than a wall of prose about it. */}
            {topic.steps && topic.steps.length > 0 ? (
              <div className="dpr-help-panel__steps">
                <h4 className="dpr-help-panel__steps-title">How to do it</h4>
                <ol className="dpr-help-panel__steps-list">
                  {topic.steps.map((step, index) => (
                    <li key={index} className="dpr-help-panel__step">
                      <p className="dpr-help-panel__step-label">{step.label}</p>
                      {step.detail ? (
                        <p className="dpr-help-panel__step-detail">{step.detail}</p>
                      ) : null}
                      {step.actions && step.actions.length > 0 ? (
                        <p className="dpr-help-panel__step-actions">
                          {step.actions.map((action) => (
                            /* A tag, not a button — see `HelpStep.actions` for why help names a
                               control rather than operating it. */
                            <span key={action} className="dpr-help-panel__tag">
                              {action}
                            </span>
                          ))}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
            {topic.glossary && topic.glossary.length > 0 ? (
              <div className="dpr-help-panel__glossary">
                <h4 className="dpr-help-panel__glossary-title">Plain-language glossary</h4>
                <dl className="dpr-help-panel__glossary-list">
                  {topic.glossary.map((entry) => (
                    <div key={entry.term} className="dpr-help-panel__glossary-entry">
                      <dt>{entry.term}</dt>
                      <dd>{entry.definition}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
          </>
        ) : (
          <EmptyState
            title="No help written for this screen yet"
            description="This screen doesn't have contextual guidance yet."
          />
        )}
      </div>
    </aside>
  );
}
